import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import { API_URL } from "../../config/api";

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "info">("info");
  const navigate = useNavigate();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");

    if (!email.trim()) {
      setMessage("Please enter your email address");
      setMessageType("error");
      return;
    }

    if (!validateEmail(email)) {
      setMessage("Please enter a valid email address");
      setMessageType("error");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage("If an account exists with this email, you will receive a reset code.");
        setMessageType("success");

        // Navigate to reset password page after short delay
        setTimeout(() => {
          navigate(ROUTE_NAMES.PAGES.FORGOT_PASSWORD + "/reset", {
            state: { email: email.toLowerCase().trim() }
          });
        }, 2000);
      } else {
        // Don't reveal if email exists or not for security
        setMessage(data.message || "If an account exists with this email, you will receive a reset code.");
        setMessageType("success");
      }
    } catch {
      setMessage("Server error. Please try again later.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
      <div
        className="w-full max-w-md rounded-2xl border p-8"
        style={{ borderColor: "var(--twc-timberwolf)", boxShadow: "var(--shadow-soft)" }}
      >
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "var(--twc-cambridge)", opacity: 0.2 }}
          >
            <svg
              className="w-8 h-8"
              style={{ color: "var(--twc-feldgrau)" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
          </div>
        </div>

        <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: "var(--twc-ink)" }}>
          Forgot your password?
        </h1>
        <p className="text-gray-600 mb-6 text-center">
          No worries! Enter your email and we'll send you a reset code.
        </p>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
              style={{ borderColor: "var(--twc-timberwolf)" }}
              disabled={loading}
              autoComplete="email"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 text-white font-medium rounded-lg transition-all disabled:opacity-50"
            style={{ backgroundColor: "var(--twc-cambridge)" }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending...
              </span>
            ) : (
              "Send Reset Code"
            )}
          </button>
        </form>

        {message && (
          <div
            className={`mt-4 p-3 rounded-lg text-sm text-center ${
              messageType === "success"
                ? "bg-green-50 text-green-700"
                : messageType === "error"
                ? "bg-red-50 text-red-700"
                : "bg-blue-50 text-blue-700"
            }`}
          >
            {message}
          </div>
        )}

        <div className="text-center mt-6">
          <Link
            to={ROUTE_NAMES.PAGES.LOGIN}
            className="text-sm font-medium hover:underline"
            style={{ color: "var(--twc-cambridge)" }}
          >
            Back to Login
          </Link>
        </div>

        <p className="text-xs text-gray-500 text-center mt-6">
          The reset code will expire in 15 minutes
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
